import { useState } from 'react';
import { Loader2, UserRoundPen, X } from 'lucide-react';
import { showToast } from '@my/ui';
import { useSetOrderInvoiceTo } from '@my/tms-api';
import { Combobox } from './inputs';

/**
 * "Invoice someone else…" under the order's Customer: the whole order's
 * charges go to another party (a forwarder paying for their client, say).
 * Saved straight away through its own endpoint — not part of the footer Save —
 * so it can be set on any saved order, even one whose header is locked. A
 * single charge can still be billed elsewhere from Charges & Costs.
 *
 * Deliberately quiet: an icon inside the Customer box (same as a charge's
 * "Bill to someone else…"), and one grey line under it once it is used. The
 * parent must be `relative` around the Customer combobox — the icon sits on
 * the input's right edge.
 */
export function InvoiceToControl({
  orderId,
  organisationId,
  customerPartyId,
  invoiceToPartyId,
  options,
}: {
  orderId: string;
  organisationId: string | null;
  customerPartyId: string;
  invoiceToPartyId: string | null;
  /** Active customers, as the Customer picker offers them. */
  options: { value: string; label: string }[];
}) {
  const setInvoiceTo = useSetOrderInvoiceTo(organisationId);
  const [editing, setEditing] = useState(false);
  const nameOf = (id: string) => options.find((o) => o.value === id)?.label ?? 'another customer';

  const save = (partyId: string | null) =>
    setInvoiceTo.mutate(
      { orderId, partyId },
      {
        onSuccess: () => {
          setEditing(false);
          showToast({
            type: 'success',
            message: partyId
              ? `Charges on this order will be invoiced to ${nameOf(partyId)}.`
              : 'Charges back to the customer.',
          });
        },
        onError: (err) =>
          showToast({ type: 'error', message: err.message || 'Could not change who is invoiced.' }),
      },
    );

  const trigger = (
    <button
      type="button"
      onClick={() => setEditing((e) => !e)}
      aria-label="Invoice someone else"
      title={invoiceToPartyId ? `Invoiced to ${nameOf(invoiceToPartyId)}` : 'Invoice someone else…'}
      className={
        'absolute right-[18px] top-[26px] z-10 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-slate-100 ' +
        (editing || invoiceToPartyId ? 'text-sky-600' : 'text-slate-300 hover:text-slate-600')
      }
    >
      <UserRoundPen className="h-3.5 w-3.5" />
    </button>
  );

  if (editing) {
    return (
      <>
        {trigger}
        <div className="mt-2.5 flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Combobox
              label="Invoice to"
              value={invoiceToPartyId ?? ''}
              onChange={(v) => v && save(v)}
              options={options.filter((o) => o.value !== customerPartyId)}
              placeholder="Who pays for this order…"
            />
          </div>
          {setInvoiceTo.isPending ? (
            <Loader2 className="mb-2 h-3.5 w-3.5 animate-spin text-slate-400" aria-hidden="true" />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="mb-1.5 text-[11px] text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          )}
        </div>
      </>
    );
  }

  if (!invoiceToPartyId) return trigger;

  return (
    <>
      {trigger}
      <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
        Invoiced to
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Change who is invoiced"
          className="font-medium text-slate-700 hover:text-purple-600 hover:underline"
        >
          {nameOf(invoiceToPartyId)}
        </button>
        <button
          type="button"
          onClick={() => save(null)}
          disabled={setInvoiceTo.isPending}
          aria-label="Invoice the customer instead"
          title="Invoice the customer instead"
          className="rounded-sm p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="h-3 w-3" />
        </button>
      </p>
    </>
  );
}
